import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import testHarness from '../../api/testHarness';
import usersApi from '../../api/users';
import useAuth from '../../hooks/useAuth';
import SimulateSwapsModal from './SimulateSwapsModal';

/**
 * Test Harness control surface. Lets an admin:
 *   - Kick off swap-post / swap-claim simulations against a PUBLISHED schedule
 *   - Wipe all test state for the district and start the flow over
 *
 * Availability simulation lives on PeriodsPanel (contextual to a period row).
 */
export default function TestHarnessPanel() {
  const { user }    = useAuth();
  const queryClient = useQueryClient();

  const [swapMode,     setSwapMode]     = useState(null);   // 'posts' | 'claims' | null
  const [confirmReset, setConfirmReset] = useState(false);
  const [toast,        setToast]        = useState(null);

  const flash = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const { data: districts = [] } = useQuery({
    queryKey: ['districts'],
    queryFn:  usersApi.getDistricts,
  });

  const myDistrict = districts.find((d) => d.id === user?.district) || districts[0];

  const resetMutation = useMutation({
    mutationFn: testHarness.reset,
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      flash(`District reset. Deleted: ${summariseCounts(data.deleted)}`);
      setConfirmReset(false);
    },
    onError: (err) => flash(err.data?.detail || err.message, 'error'),
  });

  return (
    <div className="space-y-5">
      {toast && (
        <div className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm ${
          toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-4 opacity-50 hover:opacity-100">✕</button>
        </div>
      )}

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <p className="font-semibold">Test harness mode is ON.</p>
        <p className="mt-0.5 text-amber-700">
          Actions on this page use mock SL accounts (<code>@mock.test</code>) to exercise the
          real scheduling and swap flows. Disable by unsetting <code>ALLOW_MOCK_HARNESS</code> and
          <code>DEBUG</code> in production.
        </p>
      </div>

      {/* Swap simulation */}
      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-3">
        <div>
          <h2 className="font-semibold text-gray-900">Swap marketplace simulation</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Requires at least one PUBLISHED schedule. Run "posts" first, then "claims".
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSwapMode('posts')}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Simulate swap posts
          </button>
          <button
            onClick={() => setSwapMode('claims')}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Simulate swap claims
          </button>
        </div>
      </section>

      {/* Reset */}
      <section className="rounded-xl border border-red-200 bg-white p-5 space-y-3">
        <div>
          <h2 className="font-semibold text-red-700">Reset test data</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Wipes all periods, schedules, assignments, availability, swaps, and DNP rules in{' '}
            <span className="font-semibold">{myDistrict?.name || 'your district'}</span>. Keeps
            the district, locations, buildings, mock users, and semester.
          </p>
        </div>
        <button
          onClick={() => setConfirmReset(true)}
          disabled={!myDistrict}
          className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
        >
          Reset district data
        </button>
      </section>

      {swapMode && (
        <SimulateSwapsModal
          mode={swapMode}
          onClose={() => setSwapMode(null)}
          onDone={(data) =>
            flash(
              swapMode === 'posts'
                ? `Posted ${data.posted}; skipped ${data.skipped_already_listed}.`
                : `Claimed ${data.claimed} open listings.`
            )
          }
        />
      )}

      {confirmReset && myDistrict && (
        <ConfirmResetModal
          districtName={myDistrict.name}
          onCancel={() => setConfirmReset(false)}
          onConfirm={() => resetMutation.mutate({ district_id: myDistrict.id, confirm: 'RESET' })}
          pending={resetMutation.isPending}
        />
      )}
    </div>
  );
}

function ConfirmResetModal({ districtName, onCancel, onConfirm, pending }) {
  const [typed, setTyped] = useState('');
  const ok = typed === 'RESET';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl space-y-4">
        <h2 className="text-lg font-bold text-red-700">Reset {districtName}?</h2>
        <p className="text-sm text-gray-600">
          This deletes every period, schedule, swap, and availability row in the district. Mock users
          and location setup stay. Type <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">RESET</code> to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="RESET"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onCancel}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={!ok || pending}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40">
            {pending ? 'Resetting…' : 'Reset district data'}
          </button>
        </div>
      </div>
    </div>
  );
}

function summariseCounts(counts) {
  if (!counts) return '';
  return Object.entries(counts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`)
    .join(', ') || 'nothing (already clean)';
}
